/**
 * Industry Hierarchy - Single Source of Truth
 *
 * This file defines the two-level industry structure used throughout the application:
 * - Client creation/edit modal
 * - Client Overview page display
 * - Business Scoping auto-population
 * - Profile recommendation engine
 */

export const INDUSTRY_HIERARCHY = [
  {
    value: 'Healthcare',
    label: 'Healthcare',
    subCategories: [
      { value: 'hospital', label: 'Hospital / Health System' },
      { value: 'physician-practice', label: 'Physician Practice / Clinic' },
      { value: 'dental', label: 'Dental Practice' },
      { value: 'behavioral-health', label: 'Behavioral Health' },
      { value: 'home-health', label: 'Home Health / Hospice' },
      { value: 'health-plan', label: 'Health Plan / Payer' },
      { value: 'health-it', label: 'Health IT / EHR Vendor' },
      { value: 'other-healthcare', label: 'Other Healthcare' }
    ]
  },
  {
    value: 'Financial Services',
    label: 'Financial Services',
    subCategories: [
      { value: 'banking', label: 'Banking' },
      { value: 'credit-union', label: 'Credit Union' },
      { value: 'insurance', label: 'Insurance' },
      { value: 'investment-management', label: 'Investment Management' },
      { value: 'wealth-advisory', label: 'Wealth Advisory / Financial Planning' },
      { value: 'mortgage-lending', label: 'Mortgage / Lending' },
      { value: 'fintech', label: 'FinTech' },
      { value: 'other-financial', label: 'Other Financial Services' }
    ]
  },
  {
    value: 'Non-profit',
    label: 'Nonprofit / Human Services',
    subCategories: [
      { value: 'community-services', label: 'Community Services' },
      { value: 'advocacy', label: 'Advocacy / Policy' },
      { value: 'arts-culture', label: 'Arts / Cultural' },
      { value: 'faith-based', label: 'Faith-Based Organization' },
      { value: 'foundation', label: 'Foundation / Grantmaking' },
      { value: 'behavioral-health', label: 'Behavioral Health' },
      { value: 'other-nonprofit', label: 'Other Nonprofit' }
    ]
  },
  {
    value: 'Government',
    label: 'Government',
    subCategories: [
      { value: 'local-government', label: 'Local / Municipal' },
      { value: 'county-government', label: 'County' },
      { value: 'state-government', label: 'State Agency' },
      { value: 'tribal', label: 'Tribal Government' },
      { value: 'quasi-governmental', label: 'Quasi-Governmental / Authority' },
      { value: 'other-government', label: 'Other Government' }
    ]
  },
  {
    value: 'Education',
    label: 'Education',
    subCategories: [
      { value: 'k12', label: 'K-12 / School District' },
      { value: 'higher-education', label: 'Higher Education' },
      { value: 'private-school', label: 'Private / Independent School' },
      { value: 'other-education', label: 'Other Education' }
    ]
  },
  {
    value: 'Manufacturing',
    label: 'Manufacturing',
    subCategories: null
  },
  {
    value: 'Professional Services',
    label: 'Professional Services',
    subCategories: [
      { value: 'legal', label: 'Legal' },
      { value: 'accounting-cpa', label: 'Accounting / CPA' },
      { value: 'consulting', label: 'Consulting' },
      { value: 'architecture-engineering', label: 'Architecture / Engineering' },
      { value: 'staffing', label: 'Staffing / Recruiting' },
      { value: 'other-professional', label: 'Other Professional Services' }
    ]
  },
  {
    value: 'Technology',
    label: 'Technology / Software',
    subCategories: [
      { value: 'saas', label: 'SaaS / Cloud Services' },
      { value: 'msp-mssp', label: 'MSP / MSSP' },
      { value: 'software-development', label: 'Software Development' },
      { value: 'it-services', label: 'IT Services / Consulting' },
      { value: 'other-technology', label: 'Other Technology' }
    ]
  },
  {
    value: 'Real Estate',
    label: 'Real Estate / Property Management',
    subCategories: [
      { value: 'commercial-real-estate', label: 'Commercial Real Estate' },
      { value: 'property-management', label: 'Property Management' },
      { value: 'reit', label: 'REIT / Investment Trust' },
      { value: 'residential', label: 'Residential Real Estate' },
      { value: 'other-real-estate', label: 'Other Real Estate' }
    ]
  },
  {
    value: 'Energy',
    label: 'Energy / Utilities',
    subCategories: null
  },
  {
    value: 'Retail',
    label: 'Retail / Hospitality',
    subCategories: null
  },
  {
    value: 'Transportation',
    label: 'Transportation / Logistics',
    subCategories: null
  },
  {
    value: 'Legal',
    label: 'Legal',
    subCategories: null
  },
  {
    value: 'Other',
    label: 'Other',
    subCategories: null
  }
]

/**
 * Helper: Get the primary industry object by value
 */
export function getIndustry(industryValue) {
  return INDUSTRY_HIERARCHY.find(i => i.value === industryValue) || null
}

/**
 * Helper: Get the sub-category object by industry value + sub-category value
 */
export function getSubCategory(industryValue, subCategoryValue) {
  const industry = getIndustry(industryValue)
  if (!industry?.subCategories) return null
  return industry.subCategories.find(sc => sc.value === subCategoryValue) || null
}

/**
 * Helper: Get display label for an industry + sub-category combination
 * Returns "Financial Services" or "Financial Services — Banking" depending on whether sub exists
 */
export function getIndustryDisplayLabel(industryValue, subCategoryValue) {
  const industry = getIndustry(industryValue)
  if (!industry) return industryValue || 'Not specified'

  if (!subCategoryValue) return industry.label

  const subCategory = getSubCategory(industryValue, subCategoryValue)
  if (!subCategory) return industry.label

  return `${industry.label} — ${subCategory.label}`
}

/**
 * Helper: Combine industry + sub-category into the composite key
 * used by the profile recommendation engine and Business Scoping.
 *
 * Examples:
 *   ('Healthcare', null) → 'Healthcare'
 *   ('Financial Services', 'banking') → 'Financial Services'  (Banking profile)
 *   ('Financial Services', 'insurance') → 'Financial Services' (Insurance profile)
 *
 * The composite key is what gets passed to recommendThreatProfile() as clientIndustry.
 * Sub-category is used for more specific profile matching but the base industry
 * is used for benchmark lookups.
 */
export function getCompositeIndustryKey(industryValue, subCategoryValue) {
  if (!industryValue) return null

  // For benchmarks and general industry matching, use the primary industry value
  // The sub-category affects profile selection but not the benchmark lookup
  return industryValue
}

/**
 * Helper: Get the profile signal key for threat profile matching
 * This is used by the recommendation engine to select the appropriate profile
 *
 * Examples:
 *   ('Financial Services', 'banking') → 'financial-services-banking'
 *   ('Financial Services', 'insurance') → 'financial-services-insurance'
 *   ('Healthcare', null) → 'healthcare'
 */
export function getProfileSignalKey(industryValue, subCategoryValue) {
  if (!industryValue) return null

  // Normalize industry value to lowercase with hyphens
  const normalizedIndustry = industryValue.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')

  // Industries where sub-category changes the profile signal
  const subCategoryAffectsProfile = {
    'financial-services': {
      'banking': 'financial-services-banking',
      'credit-union': 'financial-services-banking',
      'insurance': 'financial-services-insurance',
      'investment-management': 'financial-services',
      'wealth-advisory': 'financial-services',
      'mortgage-lending': 'financial-services-banking',
      'fintech': 'financial-services',
      'other-financial': 'financial-services'
    }
  }

  // Check if this industry has sub-category-specific profile keys
  const subMap = subCategoryAffectsProfile[normalizedIndustry]
  if (subMap && subCategoryValue && subMap[subCategoryValue]) {
    return subMap[subCategoryValue]
  }

  return normalizedIndustry
}

/**
 * Helper: Check if a primary industry selection has sub-categories
 */
export function hasSubCategories(industryValue) {
  const industry = getIndustry(industryValue)
  return industry?.subCategories != null && industry.subCategories.length > 0
}

/**
 * Helper: Handle legacy composite industry values from the database
 * Maps old composite values to new split values
 */
export function splitLegacyIndustry(industryValue) {
  if (!industryValue) return { industry: null, subCategory: null }

  // Map old composite values to new split values
  const legacyMap = {
    'Financial Services - Banking': { industry: 'Financial Services', subCategory: 'banking' },
    'Financial Services - Insurance': { industry: 'Financial Services', subCategory: 'insurance' },
    'financial-services-banking': { industry: 'Financial Services', subCategory: 'banking' },
    'financial-services-insurance': { industry: 'Financial Services', subCategory: 'insurance' }
  }

  if (legacyMap[industryValue]) {
    return legacyMap[industryValue]
  }

  // Not a legacy composite — return as-is
  return { industry: industryValue, subCategory: null }
}

/**
 * Helper: Get the Business Scoping bs-0 option value from industry + sub-category
 * This maps to the composite values expected by the bs-0 question options
 */
export function getBusinessScopingIndustryValue(industryValue, subCategoryValue) {
  if (!industryValue) return null

  // For Financial Services, map sub-categories to specific bs-0 values
  if (industryValue === 'Financial Services') {
    if (subCategoryValue === 'banking' || subCategoryValue === 'credit-union' || subCategoryValue === 'mortgage-lending') {
      return 'Financial Services - Banking'
    }
    if (subCategoryValue === 'insurance') {
      return 'Financial Services - Insurance'
    }
    return 'Financial Services'
  }

  // For all other industries, use the primary value directly
  return industryValue
}

/**
 * Regulatory Framework Options
 * These are the selectable framework identifiers that match the keys in
 * FRAMEWORK_OVERLAYS (orgCharacteristics.js) and rf-1 options (businessScoping.json).
 */
export const REGULATORY_FRAMEWORK_OPTIONS = [
  { value: 'hipaa', label: 'HIPAA' },
  { value: 'hitech', label: 'HITECH' },
  { value: 'pci-dss', label: 'PCI DSS' },
  { value: 'glba', label: 'GLBA' },
  { value: 'soc2', label: 'SOC 2' },
  { value: 'ccpa', label: 'CCPA / CPRA' },
  { value: 'cmmc', label: 'CMMC / DFARS' },
  { value: 'ftc-safeguards', label: 'FTC Safeguards Rule' },
  { value: 'nydfs', label: 'NYDFS 23 NYCRR 500' },
  { value: 'sec-cyber', label: 'SEC Cybersecurity Rules' },
  { value: 'ferpa', label: 'FERPA' },
  { value: 'ffiec', label: 'FFIEC' },
  { value: 'naic-mdl668', label: 'NAIC MDL-668 (Insurance Data Security)' },
  { value: 'bsa-aml', label: 'BSA/AML' },
  { value: 'gdpr', label: 'GDPR' },
  { value: 'state-privacy', label: 'State Privacy Laws' },
  { value: 'irs-4557', label: 'IRS Publication 4557' }
]

/**
 * Hardcoded mapping: industry + sub-category → default regulatory frameworks.
 *
 * Keys: "Industry" or "Industry::sub-category"
 * Values: array of framework value strings from REGULATORY_FRAMEWORK_OPTIONS
 *
 * The more specific "Industry::sub-category" key takes priority over the
 * generic "Industry" key. Both are merged (union) so industry-level defaults
 * still apply when a sub-category is selected.
 */
const INDUSTRY_FRAMEWORK_MAP = {
  // Healthcare — HIPAA + HITECH are legally inseparable; state privacy applies to non-PHI PII
  'Healthcare': ['hipaa', 'hitech', 'state-privacy'],
  'Healthcare::health-plan': ['hipaa', 'hitech', 'state-privacy'],
  'Healthcare::health-it': ['hipaa', 'hitech', 'soc2'],
  'Healthcare::dental': ['hipaa', 'hitech'],
  'Healthcare::behavioral-health': ['hipaa', 'hitech', 'state-privacy'],
  'Healthcare::home-health': ['hipaa', 'hitech'],

  // Financial Services — GLBA is foundational for ALL financial institutions
  'Financial Services': ['glba'],
  // Federally regulated depository institutions — FFIEC examinations + PCI DSS as card issuers
  'Financial Services::banking': ['glba', 'ffiec', 'pci-dss'],
  'Financial Services::credit-union': ['glba', 'ffiec', 'pci-dss'],
  'Financial Services::mortgage-lending': ['glba', 'ffiec', 'pci-dss'],
  // Non-bank financial institutions — FTC Safeguards Rule instead of FFIEC
  'Financial Services::fintech': ['glba', 'ftc-safeguards', 'pci-dss'],
  'Financial Services::other-financial': ['glba', 'ftc-safeguards'],
  // Insurance — state-regulated via NAIC model laws + GLBA
  'Financial Services::insurance': ['glba', 'naic-mdl668', 'state-privacy'],
  // Investment / Wealth — SEC Cyber Rules layer on GLBA
  'Financial Services::investment-management': ['glba', 'sec-cyber'],
  'Financial Services::wealth-advisory': ['glba', 'sec-cyber'],

  // Education — FERPA for all sub-categories
  'Education': ['ferpa'],
  'Education::higher-education': ['ferpa'],
  'Education::k12': ['ferpa'],
  'Education::private-school': ['ferpa'],
  'Education::other-education': ['ferpa'],

  // Government — state privacy as floor; NIST 800-171 for CUI handling
  'Government': ['state-privacy'],
  'Government::local-government': ['state-privacy'],
  'Government::county-government': ['state-privacy'],
  'Government::state-government': ['state-privacy'],
  'Government::tribal': ['state-privacy'],
  'Government::quasi-governmental': ['state-privacy'],

  // Professional Services — state privacy as floor; SOC 2 where contractually expected
  'Professional Services': ['state-privacy'],
  'Professional Services::legal': ['state-privacy'],
  'Professional Services::accounting-cpa': ['irs-4557', 'state-privacy'],
  'Professional Services::consulting': ['soc2', 'state-privacy'],
  'Professional Services::architecture-engineering': ['state-privacy'],
  'Professional Services::staffing': ['state-privacy'],

  // Technology — SOC 2 is standard; state privacy as floor
  'Technology': ['state-privacy'],
  'Technology::saas': ['soc2', 'state-privacy'],
  'Technology::msp-mssp': ['soc2', 'state-privacy'],
  'Technology::software-development': ['soc2', 'state-privacy'],
  'Technology::it-services': ['soc2', 'state-privacy'],

  // Manufacturing — state privacy as floor (CCPA only if processing CA data, handled via org-char)
  'Manufacturing': ['state-privacy'],

  // Energy / Utilities — state privacy as floor
  'Energy': ['state-privacy'],

  // Retail — PCI DSS for card processing; state privacy as floor
  'Retail': ['pci-dss', 'state-privacy'],

  // Transportation — state privacy as floor
  'Transportation': ['state-privacy'],

  // Legal (standalone industry)
  'Legal': ['state-privacy'],

  // Real Estate / Property Management
  'Real Estate': ['state-privacy'],
  'Real Estate::reit': ['sec-cyber', 'state-privacy'],

  // Nonprofit — state privacy as floor; behavioral health gets HIPAA
  'Non-profit': ['state-privacy'],
  'Non-profit::behavioral-health': ['hipaa', 'hitech', 'state-privacy'],
  'Non-profit::foundation': ['state-privacy']
}

/**
 * Get the default regulatory frameworks for a given industry + sub-category.
 * Merges industry-level defaults with sub-category-specific additions.
 *
 * @param {string} industry - Primary industry value (e.g., "Financial Services")
 * @param {string} subCategory - Sub-category value (e.g., "banking"), or null
 * @returns {string[]} Array of framework value strings
 */
export function getDefaultFrameworks(industry, subCategory) {
  if (!industry) return []

  const industryDefaults = INDUSTRY_FRAMEWORK_MAP[industry] || []

  if (!subCategory) return [...industryDefaults]

  const subKey = `${industry}::${subCategory}`
  const subDefaults = INDUSTRY_FRAMEWORK_MAP[subKey] || []

  // Merge and deduplicate
  const merged = [...new Set([...industryDefaults, ...subDefaults])]
  return merged
}

/**
 * Maps industries/sub-categories to default sensitive data types (dt-1 values).
 * Values correspond to dt-1 option values in businessScoping.json.
 */
const INDUSTRY_DATA_TYPE_MAP = {
  'Healthcare': ['phi', 'pii'],
  'Healthcare::health-plan': ['phi', 'pii', 'financial-data'],
  'Healthcare::health-it': ['phi', 'pii'],
  'Financial Services': ['financial-data', 'pii'],
  'Financial Services::banking': ['pci', 'financial-data'],
  'Financial Services::credit-union': ['pci', 'financial-data'],
  'Financial Services::mortgage-lending': ['financial-data'],
  'Financial Services::fintech': ['pci', 'financial-data'],
  'Financial Services::insurance': ['financial-data'],
  'Financial Services::investment-management': ['financial-data'],
  'Financial Services::wealth-advisory': ['financial-data'],
  'Education': ['pii'],
  'Education::k12': ['pii', 'coppa'],
  'Government': ['cui', 'pii'],
  'Professional Services': ['pii'],
  'Professional Services::accounting-cpa': ['financial-data', 'pii'],
  'Professional Services::legal': ['client-confidential', 'pii'],
  'Technology': ['pii'],
  'Manufacturing': ['pii'],
  'Energy': ['pii'],
  'Retail': ['pci', 'pii'],
  'Transportation': ['pii'],
  'Legal': ['client-confidential', 'pii'],
  'Real Estate': ['pii', 'financial-data'],
  'Real Estate::reit': ['financial-data', 'pii'],
  'Non-profit': ['donor-data', 'pii'],
  'Non-profit::behavioral-health': ['phi', 'donor-data'],
  'Other': ['pii']
}

/**
 * Get the default sensitive data types for a given industry + sub-category.
 */
export function getDefaultDataTypes(industry, subCategory) {
  if (!industry) return []

  const industryDefaults = INDUSTRY_DATA_TYPE_MAP[industry] || []

  if (!subCategory) return [...industryDefaults]

  const subKey = `${industry}::${subCategory}`
  const subDefaults = INDUSTRY_DATA_TYPE_MAP[subKey] || []

  const merged = [...new Set([...industryDefaults, ...subDefaults])]
  return merged
}

export default {
  INDUSTRY_HIERARCHY,
  REGULATORY_FRAMEWORK_OPTIONS,
  getIndustry,
  getSubCategory,
  getIndustryDisplayLabel,
  getCompositeIndustryKey,
  getProfileSignalKey,
  hasSubCategories,
  splitLegacyIndustry,
  getBusinessScopingIndustryValue,
  getDefaultFrameworks,
  getDefaultDataTypes
}
